import React from 'react';
import { LayoutDashboard, Users, Briefcase, CheckSquare, Upload, Settings, PoundSterling, Shield, Inbox, Phone, Handshake, Wallet, ListTodo } from 'lucide-react';
import type { SalesRoute } from '../types';

interface SidebarProps {
  currentRoute: SalesRoute;
  onNavigate: (route: SalesRoute) => void;
  isAdmin?: boolean;
  pendingPartnerApplications?: number;
}

const navItems: { route: SalesRoute; label: string; icon: React.ElementType }[] = [
  { route: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { route: 'inbox', label: 'Inbox', icon: Inbox },
  { route: 'calls', label: 'Calls', icon: Phone },
  { route: 'queue', label: "Today's Queue", icon: ListTodo },
  { route: 'leads', label: 'Leads', icon: Users },
  { route: 'deals', label: 'Deals', icon: Briefcase },
  { route: 'tasks', label: 'Tasks', icon: CheckSquare },
  { route: 'earnings', label: 'My Earnings', icon: PoundSterling },
  { route: 'import', label: 'Import', icon: Upload },
  { route: 'settings', label: 'Settings', icon: Settings },
];

const adminItems: { route: SalesRoute; label: string; icon: React.ElementType }[] = [
  { route: 'admin-commissions', label: 'Commissions', icon: Shield },
  { route: 'admin-partners', label: 'Partners', icon: Handshake },
  { route: 'admin-payouts', label: 'Payouts', icon: Wallet },
];

const Sidebar: React.FC<SidebarProps> = ({ currentRoute, onNavigate, isAdmin, pendingPartnerApplications = 0 }) => {
  return (
    <aside className="w-56 bg-slate-900 border-r border-slate-800 flex flex-col shrink-0">
      <nav className="flex-1 p-3 pt-4 space-y-1">
        {navItems.map(({ route, label, icon: Icon }) => (
          <button
            key={route}
            onClick={() => onNavigate(route)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              currentRoute === route
                ? 'bg-orange-600 text-white shadow-lg'
                : 'text-slate-300 hover:text-white hover:bg-slate-800'
            }`}
          >
            <Icon size={18} />
            {label}
          </button>
        ))}

        {isAdmin && (
          <>
            <div className="pt-4 pb-2 px-3">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Admin</span>
            </div>
            {adminItems.map(({ route, label, icon: Icon }) => (
              <button
                key={route}
                onClick={() => onNavigate(route)}
                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  currentRoute === route
                    ? 'bg-orange-600 text-white shadow-lg'
                    : 'text-slate-300 hover:text-white hover:bg-slate-800'
                }`}
              >
                <span className="flex items-center gap-3">
                  <Icon size={18} />
                  {label}
                </span>
                {route === 'admin-partners' && pendingPartnerApplications > 0 && (
                  <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                    {pendingPartnerApplications}
                  </span>
                )}
              </button>
            ))}
          </>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
